import { useTranslation } from 'react-i18next';

import { Box, forwardRef, Heading, HStack, Stack, StackProps, Text } from '@bonusly/design-system';

import { useSanitizeRichText } from '../../../../hooks/use-sanitize-rich-text';
import { IncentiveAvatar } from '../incentive-avatar';

export type IncentiveProps = StackProps & {
	avatar?: string;
	emoji?: string;
	name: string;
	description?: string;
	cta: string;
	outOfStock?: boolean;
};

const Incentive = forwardRef(({ name, description, avatar, emoji, cta, outOfStock, ...props }: IncentiveProps, ref) => {
	const { t } = useTranslation('incentives');
	const sanitizedDescription = useSanitizeRichText(description || '');

	return (
		<HStack
			alignItems="flex-start"
			as="article"
			bg="bg.surface"
			borderRadius="lg"
			gap={4}
			p={4}
			ref={ref}
			transition="box-shadow 0.25s ease-in-out"
			_hover={{ boxShadow: 'md' }}
			{...props}
		>
			<IncentiveAvatar name={name} avatar={avatar} emoji={emoji} filter={outOfStock ? 'opacity(.2)' : undefined} flexShrink={0} />

			<Stack flex={1} gap={0.5} h="100%" justifyContent="space-between" py={description ? 0 : 2.5}>
				<Box>
					{outOfStock && (
						<Text color="text.softest" fontSize={100} lineHeight={6}>
							{t('out_of_stock')}
						</Text>
					)}
					<Heading color="text.default" fontSize={150} lineHeight={6} noOfLines={description || outOfStock ? 1 : 2} overflowWrap="anywhere">
						{name}
					</Heading>
				</Box>

				{!!description && (
					<Text
						lineHeight={6}
						noOfLines={outOfStock ? 1 : 2}
						overflow="hidden"
						overflowWrap="anywhere"
						dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
					/>
				)}

				<Text color="primary.fg" fontWeight="medium" lineHeight={6} maxH={6} noOfLines={1} dangerouslySetInnerHTML={{ __html: cta }} />
			</Stack>
		</HStack>
	);
});

Incentive.displayName = 'Incentive';

export { Incentive };
